import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import DeleteItemButton from 'analysis-tool/data-frame-creation/steps/DeleteItemButton';
import {DataFrameColumnDef, DataFrameColumnType, isColumnTypeNumerical} from 'data-frames/DataFrameTypes';
import {getValidUserFacingOperationsForColumnType} from 'data-frames/filters/components/getValidOperationsForColumnType';
import {
  isListValueOperator,
  UserFacingFilter,
  UserFacingFilterOperation,
} from 'data-frames/filters/DataFrameFilterTypes';
import columnToTypeIcon from 'data-frames/util/columnToTypeIcon';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import BenchlingTypeValueInput, {
  DEFAULT_BOOLEAN_FILTER_VALUE,
} from 'data-types/components/BenchlingTypeValueInput';
import {leafTypeHasBenchlingTypeValueInput} from 'data-types/components/BenchlingTypeValueInputUtil';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {ButtonSize} from 'ui-library/buttons/Button';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import {UnitsQuery, UnitsQueryVariables} from 'units/__generated/UnitsQuery';
import UnitAwareTagValueEditor from 'units/UnitAwareTagValueEditor';
import {UNITS_QUERY} from 'units/UnitsConstants';
import {JSONValue} from 'util/Types';

import getTypeDefForFilterValue from './getTypeDefForFilterValue';

const NULL_OPERATORS: Array<UserFacingFilterOperation> = [
  UserFacingFilterOperation.IS_NULL,
  UserFacingFilterOperation.IS_NOT_NULL,
];

function getValueForNewOperator(
  oldFilter: UserFacingFilter,
  newOperator: UserFacingFilterOperation,
  columnType: DataFrameColumnType
): JSONValue {
  if (NULL_OPERATORS.includes(newOperator)) {
    return null;
  }

  if (isListValueOperator(oldFilter.operator) && !isListValueOperator(newOperator)) {
    const value = oldFilter.value as Array<JSONValue>;
    return value[0] ?? null;
  } else if (!isListValueOperator(oldFilter.operator) && isListValueOperator(newOperator)) {
    return oldFilter.value == null ? [] : [oldFilter.value];
  } else if (columnType.leafType === LeafType.TEXT && oldFilter.value == null) {
    return '';
  } else if (
    [UserFacingFilterOperation.EQUALS, UserFacingFilterOperation.DOES_NOT_EQUAL].includes(newOperator) &&
    columnType.leafType === LeafType.BOOLEAN
  ) {
    return DEFAULT_BOOLEAN_FILTER_VALUE;
  }
  return oldFilter.value;
}

interface FilterOperationSelectorProps {
  disabled: boolean;
  operation: UserFacingFilterOperation;
  onSelect: (operator: UserFacingFilterOperation) => void;
  columnType: DataFrameColumnType;
}

export function FilterOperationSelector(props: FilterOperationSelectorProps): JSX.Element {
  const allowedOperators = getValidUserFacingOperationsForColumnType(props.columnType);

  return (
    <SelectDropdown
      buttonSize={ButtonSize.MEDIUM}
      className={css(styles.selector)}
      disabled={props.disabled}
      displayName={props.operation}
      menu={
        <Menu height={MenuHeight.MEDIUM} width={MenuWidth.LARGE}>
          {allowedOperators.map((operator) => (
            <Option key={operator} value={operator} onSelect={props.onSelect}>
              {operator}
            </Option>
          ))}
        </Menu>
      }
    />
  );
}

interface FilterRowTypeValueInputProps {
  column: DataFrameColumnDef;
  filter: UserFacingFilter;
  onUpdateFilter: (updatedFilter: UserFacingFilter) => void;
  disabled: boolean;
}

function FilterRowTypeValueInput({
  column,
  filter,
  onUpdateFilter,
  disabled,
}: FilterRowTypeValueInputProps): JSX.Element {
  const valueInputComponent = (
    <BenchlingTypeValueInput
      typeDef={getTypeDefForFilterValue(filter, column)}
      allowAutoFocus={false}
      onChange={(newValue, _isValid) => {
        onUpdateFilter({...filter, value: newValue});
      }}
      value={filter.value}
      disabled={disabled}
    />
  );
  const unitApiId = column.type.unit?.unitApiId;
  return unitApiId != null && isColumnTypeNumerical(column.type) ? (
    <BenchlingQuery<UnitsQuery, UnitsQueryVariables>
      query={UNITS_QUERY}
      variables={{ids: [unitApiId]}}
      renderLoading={() => valueInputComponent}
    >
      {(data) => (
        <UnitAwareTagValueEditor editorComponent={valueInputComponent} unitSymbol={data.units[0].symbol} />
      )}
    </BenchlingQuery>
  ) : (
    valueInputComponent
  );
}

export interface FilterRowProps {
  column: DataFrameColumnDef;
  filter: UserFacingFilter;
  onDeleteFilter: () => void;
  onUpdateFilter: (updatedFilter: UserFacingFilter) => void;
  disabled: boolean;
}

/**
 * Component to configure a single filter condition.
 *
 * @package
 */
export default function FilterRow({
  column,
  filter,
  onDeleteFilter,
  onUpdateFilter,
  disabled,
}: FilterRowProps): JSX.Element {
  return (
    <div className={css(styles.filterSection)}>
      <div className={css(styles.filterItem)}>
        <Chip
          className={css(styles.chip)}
          size={ChipSize.SMALL}
          icon={columnToTypeIcon(column)}
          text={column.displayName}
          isReadonly={true}
        />

        <div className="u-tiny-spacing-left" />
        <FilterOperationSelector
          columnType={column.type}
          disabled={disabled}
          onSelect={(operator) => {
            onUpdateFilter({
              ...filter,
              operator,
              value: getValueForNewOperator(filter, operator, column.type),
            });
          }}
          operation={filter.operator}
        />
        <div className="u-tiny-spacing-left" />
        {leafTypeHasBenchlingTypeValueInput(column.type.leafType) &&
          !NULL_OPERATORS.includes(filter.operator) && (
            <FilterRowTypeValueInput
              column={column}
              filter={filter}
              onUpdateFilter={onUpdateFilter}
              disabled={disabled}
            />
          )}
      </div>
      {!disabled && <DeleteItemButton onClick={onDeleteFilter} />}
    </div>
  );
}

const styles = StyleSheet.create({
  filterItem: {
    alignItems: 'center',
    display: 'flex',
  },
  filterSection: {
    alignItems: 'center',
    display: 'flex',
  },
  chip: {
    minWidth: 125,
  },
  selector: {
    whiteSpace: 'nowrap',
  },
});
